import { useRef } from 'react';
import { Button, cn } from '@immo/ui-kit';
import { TableIcon } from '../icons';
import { useTranslations } from '../use-translations';
import { ColumnsPanel, type ColumnPanelRow } from './columns-panel';

export type ColumnsTriggerProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly rows: ReadonlyArray<ColumnPanelRow>;
  readonly onSetVisible: (id: string, visible: boolean) => void;
  readonly onReset: () => void;
  readonly onReorder: (order: ReadonlyArray<string>) => void;
  /** Id of the element naming this table — tells two „Spalten" buttons on one page apart (BL-112). */
  readonly describedBy?: string;
};

export function ColumnsTrigger({
  open,
  onOpenChange,
  rows,
  onSetVisible,
  onReset,
  onReorder,
  describedBy,
}: ColumnsTriggerProps) {
  const t = useTranslations('components.column-preference');
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="secondary"
        onClick={() => onOpenChange(!open)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-describedby={describedBy}
        // An open panel is not a filter being applied, so it paints the active-filter pair rather than `primary`.
        className={cn(
          'px-3 text-base-sm font-normal',
          open ? 'border-interactive bg-selected-bg text-text-primary' : 'text-text-secondary',
        )}
      >
        <TableIcon className="h-4 w-4" aria-hidden />
        {t('trigger')}
      </Button>
      {open && (
        <ColumnsPanel
          rows={rows}
          triggerRef={triggerRef}
          onSetVisible={onSetVisible}
          onReset={onReset}
          onReorder={onReorder}
          onClose={() => onOpenChange(false)}
        />
      )}
    </>
  );
}
